/**
 * Cloud authentication — routes all OAuth through the Central SSO Agent.
 * Legacy direct-IdP and /auth/cloud/* paths removed (security: no secrets, no weak fallbacks).
 */
import {
  beginSsoBrowserLogin,
  completeSsoBrowserCallback,
  listSsoProviders,
  sanitizeProviderId,
  SSO_CLIENT_APP_ID,
  getSsoClientAppId,
  getSsoWebRedirectUri,
} from './ssoAgentClient';
import { SSO_BLOCKED_PROVIDERS } from './cloudAuthConfig';

/** Back-compat export for redirect helper name used in docs. */
export function getRedirectUri() {
  return getSsoWebRedirectUri();
}

const ALLOWED_OAUTH_KINDS = new Set(['oauth', 'oidc', 'oauth2']);
const BLOCKED = new Set(SSO_BLOCKED_PROVIDERS);

export class CloudAuthService {
  /**
   * @param {string} providerId - e.g. google | github | microsoft
   * @param {string} mode - login | register (UX only)
   */
  static async initiateCloudAuth(providerId, mode = 'login') {
    const id = sanitizeProviderId(providerId);
    if (!id || BLOCKED.has(id)) {
      throw new Error('invalid_request');
    }
    const safeMode = mode === 'register' ? 'register' : 'login';
    await beginSsoBrowserLogin(id, safeMode);
  }

  /**
   * Handle Agent redirect to SPA /auth/callback?code=...&state=...
   * @returns {{ apiKey: string, user: object, provider?: string, cookieSession?: boolean }}
   */
  static async handleCloudCallback(_providerIgnored, code, state, _idToken) {
    // Agent already finished IdP; browser lands here with exchange code (+ state).
    // Never accept id_token or access_token from the SPA query (client-side IdP is banned).
    const params = new URLSearchParams();
    if (code) params.set('code', String(code).slice(0, 512));
    if (state) params.set('state', String(state).slice(0, 256));

    if (typeof window !== 'undefined') {
      const live = new URLSearchParams(window.location.search || '');
      // Only allowlisted query keys — drop unexpected params
      ['code', 'error', 'error_description', 'provider', 'state'].forEach((k) => {
        if (live.has(k) && !params.has(k)) {
          params.set(k, live.get(k));
        }
      });
      const hash = window.location.hash || '';
      if (hash.includes('?')) {
        const hq = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
        ['code', 'error', 'error_description', 'provider', 'state'].forEach((k) => {
          if (hq.has(k) && !params.has(k)) {
            params.set(k, hq.get(k));
          }
        });
      }
    }

    // Strip forbidden secrets if a misconfigured Agent ever put them on the URL
    params.delete('api_key');
    params.delete('token');
    params.delete('access_token');
    params.delete('id_token');
    params.delete('refresh_token');

    const session = await completeSsoBrowserCallback(params);
    if (!session || !(session.apiKey || session.cookieSession)) {
      throw new Error('account_error');
    }
    return {
      cookieSession: Boolean(session.cookieSession),
      apiKey: session.apiKey || '',
      user: {
        ...(session.user || {}),
        provider: session.provider,
        email: session.email || (session.user && session.user.email),
      },
    };
  }

  static async listEnabledOAuthProviders() {
    const data = await listSsoProviders();
    if (!data || data.enabled === false) {
      return [];
    }
    const rows = Array.isArray(data.providers) ? data.providers : [];
    const out = [];
    const seen = new Set();
    for (const p of rows) {
      if (!p || p.enabled === false) continue;
      const id = sanitizeProviderId(p.id);
      if (!id || seen.has(id) || BLOCKED.has(id)) continue;
      const kind = String(p.kind || 'oauth').toLowerCase();
      if (kind === 'password' || !ALLOWED_OAUTH_KINDS.has(kind)) continue;
      seen.add(id);
      out.push({
        id,
        display_name:
          typeof p.display_name === 'string' && p.display_name.trim()
            ? p.display_name.trim().slice(0, 48)
            : id,
        kind: kind === 'oidc' || kind === 'oauth2' ? 'oauth' : kind,
        enabled: true,
        sort_order: Number.isFinite(p.sort_order) ? p.sort_order : 100,
      });
    }
    out.sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
    return out;
  }

  static getClientAppId() {
    return getSsoClientAppId();
  }
}

export default CloudAuthService;
